import React, { useState, useRef, useEffect } from 'react';
import { norm } from '../utils/buscar';

/**
 * Filtro reutilizable. El primer campo tipo 'input' (la búsqueda) queda FIJO
 * en la misma línea, a la izquierda del icono de embudo. Los demás campos
 * (selects, etc.) se despliegan desde el embudo. Mismo diseño en toda la app.
 *
 * fields: [{ tipo:'select'|'input', label, value, onChange, options?, placeholder?, width?, capitalize?, suggestions? }]
 * onClear: callback opcional; muestra "Limpiar" cuando hay algún filtro activo.
 */
export default function FiltroPanel({ fields = [], onClear }) {
  const [open, setOpen] = useState(false);
  const [sugOpen, setSugOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  // Búsqueda fija = primer campo tipo 'input'. El resto va en el panel del embudo.
  const searchIdx = fields.findIndex(f => f.tipo === 'input');
  const search = searchIdx >= 0 ? fields[searchIdx] : null;
  const rest = fields.filter((_, i) => i !== searchIdx);
  const restActivo = rest.some(f => f.value !== '' && f.value != null);

  return (
    <div ref={ref} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      {/* ── Búsqueda fija ── */}
      {search && (
        <div style={{ position: 'relative' }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"
               strokeLinecap="round" strokeLinejoin="round"
               style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
          </svg>
          <input
            style={{ ...s.fInput, width: search.width || 220, paddingLeft: 32, paddingRight: search.value ? 28 : 11 }}
            placeholder={search.placeholder || search.label || 'Buscar…'}
            value={search.value}
            onChange={e => { search.onChange(e.target.value); if (search.suggestions) setSugOpen(true); }}
            onFocus={() => { if (search.suggestions) setSugOpen(true); }}
            onBlur={() => setTimeout(() => setSugOpen(false), 150)}
          />
          {search.value && (
            <button type="button" title="Limpiar búsqueda" onClick={() => search.onChange('')}
                    style={s.clearX}>✕</button>
          )}
          {search.suggestions && sugOpen && (() => {
            const q = norm(search.value);
            const matches = search.suggestions.filter(o => !q || norm(o).includes(q)).slice(0, 8);
            if (!matches.length) return null;
            return (
              <div style={s.sugBox}>
                {matches.map((o, j) => (
                  <div key={j} style={s.sugItem}
                       onMouseDown={() => { search.onChange(o); setSugOpen(false); }}>{o}</div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Embudo con el resto de filtros ── */}
      {rest.length > 0 && (
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <button type="button" title={restActivo ? 'Filtros activos' : 'Filtrar'}
                  style={{ ...s.iconBox, ...(restActivo ? s.iconBoxActive : {}) }}
                  onClick={() => setOpen(o => !o)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" /></svg>
          </button>
          {open && (
            <div style={s.panel}>
              {rest.map((f, i) => (
                <div key={i} style={{ width: f.width || (f.tipo === 'input' ? 210 : 160) }}>
                  <div style={s.label}>{f.label}</div>
                  {f.tipo === 'input' ? (
                    <input style={{ ...s.fInput, width: '100%', minWidth: 0 }} placeholder={f.placeholder || ''}
                           value={f.value} onChange={e => f.onChange(e.target.value)} />
                  ) : (
                    <select style={{ ...s.fInput, width: '100%', minWidth: 0, textTransform: f.capitalize ? 'capitalize' : 'none' }}
                            value={f.value} onChange={e => f.onChange(e.target.value)}>
                      {(f.options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  )}
                </div>
              ))}
              {(restActivo || (search && search.value)) && onClear && (
                <div style={{ alignSelf: 'flex-end' }}>
                  <button type="button" style={s.clearBtn} onClick={() => { onClear(); setOpen(false); }}>Limpiar</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const s = {
  iconBox: { height: 38, width: 38, boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#94a3b8', cursor: 'pointer', padding: 0, flexShrink: 0 },
  iconBoxActive: { background: '#1d4ed8', borderColor: '#1d4ed8', color: '#fff' },
  panel: { position: 'absolute', top: 0, right: 'calc(100% + 8px)', zIndex: 50, background: '#0f172a', border: '1px solid #334155', borderRadius: 12, padding: 16, display: 'flex', gap: 20, alignItems: 'flex-start', boxShadow: '0 16px 40px rgba(0,0,0,.5)' },
  label: { color: '#64748b', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.03em', marginBottom: 6 },
  fInput: { background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: '0 11px', height: 38, boxSizing: 'border-box', color: '#f1f5f9', fontSize: 13 },
  clearX: { position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 3 },
  clearBtn: { background: '#1e293b', border: '1px solid #334155', color: '#94a3b8', height: 38, boxSizing: 'border-box', borderRadius: 8, padding: '0 14px', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' },
  sugBox: { position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 60, background: '#0f172a', border: '1px solid #334155', borderRadius: 8, maxHeight: 240, overflowY: 'auto', boxShadow: '0 12px 32px rgba(0,0,0,.5)' },
  sugItem: { padding: '8px 11px', fontSize: 13, color: '#e2e8f0', cursor: 'pointer', borderBottom: '1px solid #1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
};
